import { computed, inject, Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';

import { DealerProvider } from '../../../auth/dealer/dealer.provider';
import { CountryConfigurationFeature, CountryConfigurationSetting } from '../../../auth/user/user';
import { UserService } from '../../../auth/user/user.service';
import { AddressField } from '../model/address-field.model';
import { CommunicationField } from '../model/communication-field.model';
import { ConsentType } from '../model/consent-type.model';
import { C2vOperation } from '../model/customer-vehicle.model';
import { IdentityField } from '../model/identity-field.model';
import { InsightsType } from '../model/insights.model';
import { RetailFeature } from '../model/retail-feature.model';
import { VehicleField } from '../model/vehicle-field.model';

export type Feature =
  | CommunicationField
  | IdentityField
  | AddressField
  | ConsentType
  | VehicleField
  | InsightsType
  | RetailFeature
  | C2vOperation;

@Injectable({
  providedIn: 'root',
})
export class FeatureVisibilityService {
  private readonly dealerProvider = inject(DealerProvider);
  private readonly userService = inject(UserService);
  private readonly logger = inject(NGXLogger);

  static readonly featureMapping: Record<CountryConfigurationFeature, Feature> = {
    [CountryConfigurationFeature.IDENTITY_SUFFIX]: IdentityField.SUFFIX,
    [CountryConfigurationFeature.IDENTITY_TITLE]: IdentityField.TITLE,
    [CountryConfigurationFeature.IDENTITY_MIDDLE_NAME]: IdentityField.MIDDLE_NAME,
    [CountryConfigurationFeature.IDENTITY_NATIONAL_ID]: IdentityField.NATIONAL_ID,
    [CountryConfigurationFeature.IDENTITY_NATIONAL_ID_DOCUMENT_TYPE]: IdentityField.NATIONAL_ID_DOCUMENT_TYPE,
    [CountryConfigurationFeature.IDENTITY_TAX_NUMBER]: IdentityField.TAX_NUMBER,
    [CountryConfigurationFeature.DEALER_CONSENT]: ConsentType.DEALER_CONSENT,
    [CountryConfigurationFeature.ADDRESS_PROVINCE]: AddressField.PROVINCE,
    [CountryConfigurationFeature.LICENSE_SEARCH]: VehicleField.LICENSE_SEARCH,
    [CountryConfigurationFeature.VIN_SEARCH]: VehicleField.VIN_SEARCH,
    [CountryConfigurationFeature.CUSTOMER_INSIGHTS]: InsightsType.CUSTOMER_INSIGHTS,
    [CountryConfigurationFeature.WORKSHOP_CUSTOMER_STATUS]: RetailFeature.WORKSHOP_CUSTOMER_STATUS,
    [CountryConfigurationFeature.C2V_REMOVE_VEHICLES]: C2vOperation.REMOVE_VEHICLES,
    [CountryConfigurationFeature.C2V_REMOVE_RELATIONS]: C2vOperation.REMOVE_RELATIONS,
    [CountryConfigurationFeature.C2V_ADD_VEHICLES]: C2vOperation.ADD_VEHICLES,
    [CountryConfigurationFeature.C2V_ADD_RELATIONS]: C2vOperation.ADD_RELATIONS,
    [CountryConfigurationFeature.C2V_EDIT_LICENSE_PLATE]: C2vOperation.EDIT_LICENSE_PLATE,
  };

  isHidden(feature: Feature) {
    return this.hiddenFeatures().includes(feature);
  }

  anyHidden(...features: Feature[]) {
    return features.some((feature) => this.isHidden(feature));
  }

  allHidden(...features: Feature[]) {
    return features.every((feature) => this.isHidden(feature));
  }

  private readonly hiddenFeatures = computed(() => {
    const countryConfigs = this.userService.countryConfiguration();
    const countryCode = this.dealerProvider.activeCountry();
    if (!this.userService.userSignal()) {
      return []; // skip evaluation in the initialization phase
    } else if (!countryConfigs || !countryCode || !countryConfigs[countryCode]) {
      throw new Error(
        `#CDFTF0207 Failed to determine feature configuration. There are no available feature flag configs for this user in the country ${countryCode}. These countries are available: [${Object.keys(countryConfigs ?? {}).join(', ')}]`,
      );
    }

    const settings = countryConfigs[countryCode];
    const hiddenFeatures = Object.values(CountryConfigurationFeature)
      .filter((configFeature) => this.isFeatureHidden(settings, configFeature))
      .map((configFeature) => FeatureVisibilityService.featureMapping[configFeature]);

    this.logger.log('Following functions are disabled in the active country: ', hiddenFeatures);
    return hiddenFeatures;
  });

  private isFeatureHidden(settings: CountryConfigurationSetting[], feature: CountryConfigurationFeature): boolean {
    return settings.find((s) => s.feature == feature)?.resolvedValue !== true;
  }
}
